package com.fantasytenis.web.components.skills

import androidx.compose.runtime.Composable
import com.fantasytenis.shared.domain.lineup.entity.Category
import com.fantasytenis.shared.domain.skills.entity.SkillTier
import com.fantasytenis.web.theme.Colors
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * Size variants for skill bar
 */
enum class SkillBarSize(val height: Int, val fontSize: Int) {
    SMALL(4, 10),
    MEDIUM(8, 12),
    LARGE(12, 14)
}

/**
 * Get color for category
 */
fun getCategoryColor(category: Category): CSSColorValue {
    return when (category) {
        Category.SERVE -> Colors.Category.Serve
        Category.FOREHAND -> Colors.Category.Forehand
        Category.BACKHAND -> Colors.Category.Backhand
        Category.VOLLEYS -> Colors.Category.Volleys
        Category.FIGHTING -> Colors.Category.Fighting
    }
}

/**
 * Get tier emoji badge
 */
fun getTierEmoji(tier: SkillTier): String {
    return when (tier) {
        SkillTier.ELITE -> "🔥"
        SkillTier.STRONG -> "⭐"
        SkillTier.SOLID -> "💪"
        SkillTier.DEVELOPING -> "📈"
    }
}

/**
 * Skill bar component showing a colored progress bar for a skill cost
 *
 * @param category The skill category (determines color)
 * @param value The skill cost value (1-12)
 * @param showTier Whether to show the tier badge
 * @param showLabel Whether to show the category label
 * @param size The size variant
 */
@Composable
fun SkillBar(
    category: Category,
    value: Int,
    showTier: Boolean = false,
    showLabel: Boolean = false,
    size: SkillBarSize = SkillBarSize.MEDIUM
) {
    val color = getCategoryColor(category)
    val fillPercent = (value.coerceIn(1, 12) / 12.0 * 100).toInt()
    val tier = SkillTier.fromCost(value)

    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(8.px)
            width(100.percent)
        }
    }) {
        // Category label (optional)
        if (showLabel) {
            Span({
                style {
                    fontSize(size.fontSize.px)
                    fontWeight(500)
                    color(Colors.TextSecondary)
                    width(70.px)
                    flexShrink(0)
                }
            }) {
                Text(category.displayName)
            }
        }

        // Progress bar container
        Div({
            style {
                flex(1)
                height(size.height.px)
                backgroundColor(Colors.Gray200)
                borderRadius((size.height / 2).px)
                overflow("hidden")
            }
        }) {
            // Filled portion
            Div({
                style {
                    width(fillPercent.percent)
                    height(100.percent)
                    backgroundColor(color)
                    borderRadius((size.height / 2).px)
                    property("transition", "width 0.3s ease")
                }
            })
        }

        // Value display
        Span({
            style {
                fontSize(size.fontSize.px)
                fontWeight(600)
                color(Colors.TextPrimary)
                minWidth(20.px)
                textAlign("right")
            }
        }) {
            Text(value.toString())
        }

        // Tier badge (optional)
        if (showTier) {
            Span({
                style {
                    fontSize(size.fontSize.px)
                }
            }) {
                Text(getTierEmoji(tier))
            }
        }
    }
}

/**
 * Mini skill bar for compact displays (no label, just bar + value)
 */
@Composable
fun MiniSkillBar(
    category: Category,
    value: Int
) {
    val color = getCategoryColor(category)
    val fillPercent = (value.coerceIn(1, 12) / 12.0 * 100).toInt()

    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(4.px)
        }
    }) {
        // Mini progress bar
        Div({
            style {
                width(40.px)
                height(4.px)
                backgroundColor(Colors.Gray200)
                borderRadius(2.px)
                overflow("hidden")
            }
        }) {
            Div({
                style {
                    width(fillPercent.percent)
                    height(100.percent)
                    backgroundColor(color)
                }
            })
        }

        // Value
        Span({
            style {
                fontSize(10.px)
                fontWeight(600)
                color(Colors.TextSecondary)
            }
        }) {
            Text(value.toString())
        }
    }
}
